import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import {
    PageBreadcrumb,
    TextButton,
    Text,
    Spacer,
} from '@zillow/constellation';
import { Container, Notification } from '@zillow/zrs-comet';

import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import { USER_MANAGEMENT_PAGE } from 'app/shared/constants/RouteConstants';
import UserPermissionsConstants from 'app/shared/constants/UserPermissionsConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import { RMWContext } from 'app/shared/providers/RMWContext';
import { UserManagementSelectors, UserSelectors } from 'app/shared/selectors';
import UserManagementActions from 'app/shared/actions/UserManagementActions';
import queryUtils from 'app/shared/utils/queryUtils';
import Infos from './Infos';
import Questions from './Questions';
import Role from './Role';
import PropertyPicker from './PropertyPicker';
import Permissions from './Permissions';
import Preview from './Preview';

import './style.scss';

const propTypes = {
    user: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
};

const AddUserPage = ({
    user,
    dispatch,
    currentUser,
    location,
}) => {
    const { paths } = React.useContext(RMWContext);
    const userManagementPath = `${paths.userManagementPage}/${paths.userManagementUsers}`;
    const queryParams = queryUtils.parse(location.search);
    const isInvitation = location.pathname.includes(paths.userManagementAddUser);
    const isPending = !!queryParams.email;
    const isYou = !isInvitation && !isPending && user.userToken === currentUser.userToken;
    const [activeStep, setActiveStep] = React.useState(isInvitation ? 1 : 6);

    React.useEffect(() => {
        if (isInvitation) {
            dispatch(UserManagementActions.initInvitation());
        } else if (isPending) {
            // todo get pending data
            dispatch(UserManagementActions.getPendingProfile(queryParams.email));
            dispatch(UserManagementActions.getPendingUserProperties(queryParams.email));
        } else {
            dispatch(UserManagementActions.getCurrentUserInfo(queryParams.userBrand,
                queryParams.userToken));
            dispatch(UserManagementActions.getCurrentUserProperties(queryParams.userBrand, queryParams.userToken));
        }
        dispatch(UserManagementActions.getAvailableProperties('zillow', user.userToken));
        return () => {
            dispatch(UserManagementActions.currentUserInit());
        };
    }, []);

    const handleGoToUserManagement = () => {
        window.router.transitionTo(userManagementPath);
    };

    const handleSave = () => {
        dispatch(UserManagementActions.editableUserSave())
            .then(() => {
                window.router.transitionTo(`${userManagementPath}?name=${user.userName}`);
            });
    };

    const isAdmin = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN];
    const allProperties = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES];

    const handleNextStep = () => {
        setActiveStep(activeStep + 1);
    };

    const handlePreviousStep = () => {
        setActiveStep(isInvitation ? activeStep - 1 : 6);
    };

    const handleSaveComplete = () => setActiveStep(6);

    const renderStep = () => {
        switch (activeStep) {
            case 1:
                return (
                    <Infos
                        onNextStep={() => setActiveStep(2)}
                        onCancel={handleGoToUserManagement}
                    />
                );
            case 2:
                return (
                    <Role
                        onNextStep={() => setActiveStep(isAdmin ? 6 : 3)}
                        onCancel={handleGoToUserManagement}
                        onBack={handlePreviousStep}
                        handleSaveComplete={handleSaveComplete}
                    />
                );
            case 3:
                return (
                    <Questions
                        properties={currentUser.properties}
                        onNextStep={() => setActiveStep(allProperties ? 5 : 4)}
                        onCancel={handleGoToUserManagement}
                        onBack={handlePreviousStep}
                    />
                );
            case 4:
                return (
                    <PropertyPicker
                        onNextStep={handleNextStep}
                        onCancel={handleGoToUserManagement}
                        onBack={handlePreviousStep}
                        isInvitation={isInvitation}
                    />
                );
            case 5:
                return (
                    <Permissions
                        onNextStep={handleNextStep}
                        onCancel={handleGoToUserManagement}
                        handleSaveComplete={handleSaveComplete}
                        onBack={() => {
                            if (allProperties) {
                                setActiveStep(3);
                            } else {
                                handlePreviousStep();
                            }
                        }}
                    />
                );
            case 6:
                return (
                    <Preview
                        onEditRole={() => setActiveStep(2)}
                        onEditAllProperties={() => setActiveStep(3)}
                        onEditProperties={() => setActiveStep(4)}
                        onSendInvite={handleSave}
                        onBack={() => {
                            if (isAdmin) {
                                setActiveStep(2);
                            } else if (allProperties) {
                                setActiveStep(3);
                            } else {
                                handlePreviousStep();
                            }
                        }}
                        onCancel={handleGoToUserManagement}
                        invitationLoadingState={currentUser.userLoadingState}
                        onEditPermissions={() => setActiveStep(5)}
                    />
                );
            default:
                return null;
        }
    };

    const getTitle = () => {
        if (isInvitation) {
            return 'Add a new user';
        }
        if (isYou) {
            return 'Your details';
        }
        return 'User details';
    };

    const renderContent = () => (
        <React.Fragment>
            {currentUser.userErrorMessage && (
                <Spacer marginBottom="xs">
                    <Notification type="error">
                        {currentUser.userErrorMessage}
                    </Notification>
                </Spacer>
            )}
            <Text fontType="h6">{getTitle()}</Text>
            {!currentUser.userErrorMessage && renderStep()}
        </React.Fragment>
    );

    const { userLoadingState } = currentUser;
    const isLoading = userLoadingState === LoadingConstants.LOADING
        || userLoadingState === LoadingConstants.INITIAL;

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div className="AddUserPage">
                <HeadWrapper title="Add user" />
                <PermissionCheck route={USER_MANAGEMENT_PAGE}>
                    <Container className="AddUserPage-container">
                        <PageBreadcrumb
                            backButton={(
                                <TextButton
                                    as={Link}
                                    to={userManagementPath}
                                >
                                    User management
                                </TextButton>
                            )}
                        />
                        <div className="AddUserPage-card">
                            {isLoading ? <SpinnerContainer /> : renderContent()}
                        </div>
                    </Container>
                </PermissionCheck>
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    editableProperties: UserManagementSelectors.makeEditableProperties(state),
    currentUser: state.userManagement.currentUser,
});

AddUserPage.propTypes = propTypes;

export { AddUserPage };
export default connect(mapStateToProps)(AddUserPage);
